// BookingPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// The URL for your new backend server
//const BACKEND_URL = 'http://localhost:3001';
const BACKEND_URL = 'https://api.meeting.csttraining.online';

const BookingPage = () => {
    const navigate = useNavigate();
    
    const [sessionData, setSessionData] = useState({
        guid: null,
        firstName: '',
        lastName: '',
        email: '',
        assessorCalendarId: null,
        isValidated: false,
        isLoading: true
    });

    const [schedulingConfig, setSchedulingConfig] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [error, setError] = useState(null);

    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isBooking, setIsBooking] = useState(false);

    // --- Fetch Scheduling Configuration ---
    useEffect(() => {
        const fetchSchedulingConfig = async () => {
            try {
                const response = await fetch(`${BACKEND_URL}/api/scheduling-config`);
                if (!response.ok) {
                    throw new Error('Failed to fetch scheduling configuration');
                }
                const config = await response.json();
                setSchedulingConfig(config);
            } catch (err) {
                console.error('Failed to fetch scheduling config:', err);
                // Fallback to default config
                setSchedulingConfig({
                    bufferEnabled: false,
                    bufferMinutes: 15,
                    schedulingIntervals: [0, 15, 30, 45],
                    meetingDurationMinutes: 45,
                    workingHours: { start: 9, end: 17 }
                });
            }
        };

        fetchSchedulingConfig();
    }, []);

    // --- Session Validation ---
    useEffect(() => {
        const validateSession = async () => {
            // Parse URL parameters for session key
            const urlParams = new URLSearchParams(window.location.search);
            const sessionKey = urlParams.get('key');

            if (!sessionKey) {
                setError("Invalid Access: A valid session key has been provided");
                setSessionData(prev => ({ ...prev, isLoading: false }));
                return;
            }

            try {
                const response = await fetch(`${BACKEND_URL}/api/validate-session/${sessionKey}`);
                
                if (!response.ok) {
                    const errorData = await response.json();
                    if (errorData.expired) {
                        setError("Session Expired: Your session has expired or is invalid. Please request a new booking link.");
                    } else {
                        setError("Session Error: Unable to validate your session. Please try again or request a new booking link.");
                    }
                    setSessionData(prev => ({ ...prev, isLoading: false }));
                    return;
                }

                const data = await response.json();
                setSessionData({
                    guid: sessionKey,
                    firstName: data.user.firstName,
                    lastName: data.user.lastName,
                    email: data.user.email,
                    assessorCalendarId: data.assessorCalendarId,
                    isValidated: true,
                    isLoading: false
                });

            } catch (err) {
                console.error('Session validation error:', err);
                setError("Connection Error: Unable to validate your session. Please check your internet connection and try again.");
                setSessionData(prev => ({ ...prev, isLoading: false }));
            }
        };

        validateSession();
    }, []);

    // Fetch availability when the component mounts or the date/assessor changes
    useEffect(() => {
        if (sessionData.assessorCalendarId && sessionData.isValidated && schedulingConfig) {
            getAvailableSlots(selectedDate);
        }
    }, [selectedDate, sessionData.assessorCalendarId, sessionData.isValidated, schedulingConfig]);

    // --- Helper function to generate potential slots ---
    const generatePotentialSlots = useCallback((date, config) => {
        const slots = [];
        
        for (let hour = config.workingHours.start; hour < config.workingHours.end; hour++) {
            for (const minutes of config.schedulingIntervals) {
                const slotStart = new Date(date);
                slotStart.setHours(hour, minutes, 0, 0);
                const slotEnd = new Date(slotStart.getTime() + config.meetingDurationMinutes * 60000);
                
                // Only add slot if it ends within working hours
                if (slotEnd.getHours() < config.workingHours.end || 
                    (slotEnd.getHours() === config.workingHours.end && slotEnd.getMinutes() === 0)) {
                    slots.push({
                        start: new Date(slotStart),
                        end: new Date(slotEnd)
                    });
                }
            }
        }
        
        return slots;
    }, []);

    // --- Data Fetching ---
    const getAvailableSlots = useCallback(async (date) => {
        if (!sessionData.assessorCalendarId || !schedulingConfig) return;

        setIsLoadingSlots(true);
        setError(null);
        setAvailableSlots([]);

        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        try {
            // Call backend's /api/free-busy endpoint (backend applies buffer if enabled)
            const response = await fetch(`${BACKEND_URL}/api/free-busy?calendarId=${sessionData.assessorCalendarId}&start=${startOfDay.toISOString()}&end=${endOfDay.toISOString()}`);
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || 'Failed to fetch availability from server.');
            }
            
            const busySlots = await response.json();

            // Generate potential slots using quarter-hour intervals from config
            const potentialSlots = generatePotentialSlots(date, schedulingConfig);

            // Filter out overlapping and past slots
            const available = potentialSlots.filter(slot => {
                const isOverlapping = busySlots.some(busySlot =>
                    (slot.start < new Date(busySlot.end)) && (slot.end > new Date(busySlot.start))
                );
                const isPast = slot.start <= new Date();
                return !isOverlapping && !isPast;
            });

            setAvailableSlots(available);

        } catch (err) {
            console.error('Error fetching availability:', err);
            setError(`Could not fetch the assessor's availability. ${err.message}`);
        } finally {
            setIsLoadingSlots(false);
        }
    }, [sessionData.assessorCalendarId, schedulingConfig, generatePotentialSlots]);

    // --- .ics File Generation ---
    const generateICS = (slot, details) => {
        const toICSDate = (date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const event = {
            uid: `${Date.now()}${details.email}`,
            dtstamp: toICSDate(new Date()),
            dtstart: toICSDate(slot.start),
            dtend: toICSDate(slot.end),
            summary: `Meeting: ${details.firstName} ${details.lastName} & Assessor`,
            description: `Booking for delegate ${details.firstName} ${details.lastName} (${details.email}).`,
            organizer: `mailto:${details.assessorCalendarId}`,
            attendee: `mailto:${details.email}`,
        };

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//CST Calendar App//EN',
            'BEGIN:VEVENT',
            `UID:${event.uid}`,
            `DTSTAMP:${event.dtstamp}`,
            `DTSTART:${event.dtstart}`,
            `DTEND:${event.dtend}`,
            `SUMMARY:${event.summary}`,
            `DESCRIPTION:${event.description}`,
            `ORGANIZER;CN=Assessor:${event.organizer}`,
            `ATTENDEE;CN=${details.firstName} ${details.lastName};ROLE=REQ-PARTICIPANT:${event.attendee}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');
        
        return icsContent;
    };

    // --- Event Handlers ---
    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot);
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        setIsBooking(true);
        setError(null);

        try {
            // Use the new session-based booking endpoint
            const appointmentDetails = {
                start: { dateTime: selectedSlot.start.toISOString(), timeZone: 'Europe/London' },
                end: { dateTime: selectedSlot.end.toISOString(), timeZone: 'Europe/London' }
            };

            const response = await fetch(`${BACKEND_URL}/api/book-appointment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    guid: sessionData.guid,
                    appointmentDetails
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.expired) {
                    setError("Session Expired: Your session has expired. Please request a new booking link.");
                } else {
                    throw new Error(errorData.details || 'Failed to book appointment.');
                }
                return;
            }
            
            // Generate and download the .ics file for the delegate
            const icsData = generateICS(selectedSlot, sessionData);
            const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
    
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'appointment.ics');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Navigate to confirmation page with appointment details
            navigate('/confirmation', {
                state: {
                    appointmentDetails: {
                        date: selectedSlot.start.toLocaleDateString(),
                        time: selectedSlot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        attendeeName: `${sessionData.firstName} ${sessionData.lastName}`,
                        attendeeEmail: sessionData.email
                    }
                }
            });
            
        } catch (err) {
            console.error("Booking failed:", err);
            setError(`There was an error creating the appointment: ${err.message}`);
        } finally {
            setIsBooking(false);
            setSelectedSlot(null);
        }
    };
    
    return (
        <div className="bg-gray-900 text-white min-h-screen font-sans flex flex-col items-center p-4 sm:p-6 md:p-8">
            <div className="w-full max-w-6xl mx-auto">
                <header className="flex justify-between items-center mb-6 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-400">Book a Meeting with an Assessor</h1>
                </header>

                {/* Loading state */}
                {sessionData.isLoading && (
                    <div className="flex justify-center items-center h-48">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                        <span className="ml-4 text-lg">Validating session...</span>
                    </div>
                )}
                
                {/* Error state */}
                {error && <div className="bg-red-800 text-red-100 p-4 rounded-lg mb-6 break-words">{error}</div>}

                {/* Main application - only show when session is validated */}
                {sessionData.isValidated && !sessionData.isLoading && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                        <div className="md:col-span-3 flex flex-col gap-8">
                            <Calendar selectedDate={selectedDate} onDateSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }} />
                            {selectedSlot && (
                                <BookingForm 
                                    slot={selectedSlot}
                                    details={sessionData}
                                    onSubmit={handleBookingSubmit}
                                    isBooking={isBooking}
                                />
                            )}
                        </div>

                        <div className="md:col-span-2">
                           <TimeSlots
                                isLoading={isLoadingSlots}
                                slots={availableSlots}
                                onSlotSelect={handleSlotSelect}
                                selectedSlot={selectedSlot}
                                selectedDate={selectedDate}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Sub-components ---

const BookingForm = ({ slot, details, onSubmit, isBooking }) => {
    return (
        <div className="bg-gray-800 p-6 rounded-lg shadow-xl animate-fade-in">
             <h3 className="font-bold text-xl mb-4">Confirm Your Appointment</h3>
             <p className="mb-6">You are booking the slot for <span className="font-bold text-blue-400">{slot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> on <span className="font-bold text-blue-400">{slot.start.toLocaleDateString()}</span>.</p>
            <form onSubmit={onSubmit}>
                <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Your Name</label>
                    <input 
                        type="text" 
                        id="name"
                        value={`${details.firstName} ${details.lastName}`}
                        readOnly
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 focus:outline-none cursor-not-allowed"
                    />
                </div>
                 <div className="mb-6">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Your Email</label>
                    <input 
                        type="email" 
                        id="email"
                        value={details.email}
                        readOnly
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 focus:outline-none cursor-not-allowed"
                    />
                </div>
                <button 
                    type="submit"
                    disabled={isBooking}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-500"
                >
                    {isBooking ? "Booking..." : "Confirm & Book Appointment"}
                </button>
            </form>
        </div>
    );
};

const Calendar = ({ selectedDate, onDateSelect }) => {
    const [date, setDate] = useState(new Date(selectedDate));

    useEffect(() => { setDate(new Date(selectedDate)); }, [selectedDate]);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const startDay = firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();
    
    const today = new Date();
    today.setHours(0,0,0,0);

    const changeMonth = (offset) => {
        setDate(new Date(date.getFullYear(), date.getMonth() + offset, 1));
    };
    
    const renderDays = () => {
        const days = [];
        for (let i = 0; i < startDay; i++) {
            days.push(<div key={`empty-${i}`} className="p-2 text-center"></div>);
        }
        for (let i = 1; i <= totalDays; i++) {
            const dayDate = new Date(date.getFullYear(), date.getMonth(), i);
            const isSelected = dayDate.toDateString() === selectedDate.toDateString();
            const isToday = dayDate.toDateString() === today.toDateString();
            const isPast = dayDate < today;

            let dayClasses = "p-2 text-center rounded-full cursor-pointer transition-colors w-10 h-10 flex items-center justify-center";
            if(isPast) {
                dayClasses += " text-gray-600 cursor-not-allowed";
            } else if (isSelected) {
                dayClasses += " bg-blue-600 text-white font-bold";
            } else if (isToday) {
                dayClasses += " bg-gray-700 text-blue-300";
            } else {
                dayClasses += " hover:bg-gray-700";
            }
            
            days.push(
                <div key={i} className={dayClasses} onClick={() => !isPast && onDateSelect(dayDate)}>
                    {i}
                </div>
            );
        }
        return days;
    };
    
    return (
        <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-gray-700">&lt;</button>
                <h3 className="text-xl font-semibold">{monthNames[date.getMonth()]} {date.getFullYear()}</h3>
                <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-gray-700">&gt;</button>
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {daysOfWeek.map(day => <div key={day} className="font-bold text-xs text-gray-400 text-center">{day}</div>)}
                {renderDays()}
            </div>
        </div>
    );
};

const TimeSlots = ({ isLoading, slots, onSlotSelect, selectedSlot, selectedDate }) => {
    const formatDate = (date) => {
        return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date);
    };

    return (
        <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl h-full flex flex-col">
            <h3 className="font-semibold text-lg mb-4 text-center">{formatDate(selectedDate)}</h3>
            {isLoading ? (
                <div className="flex justify-center items-center flex-1">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                </div>
            ) : (
                <div className="flex-1 overflow-hidden">
                    <div className="custom-scrollbar flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-2">
                        {slots.length > 0 ? slots.map(slot => {
                            const isSelected = selectedSlot && slot.start.getTime() === selectedSlot.start.getTime();
                            return (
                                <button
                                    key={slot.start.toISOString()}
                                    onClick={() => onSlotSelect(slot)}
                                    className={`p-3 rounded-lg text-center font-semibold transition-colors w-full ${
                                        isSelected ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-blue-500'
                                    }`}
                                >
                                    {slot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </button>
                            )
                        }) : <p className="text-center text-gray-400 pt-8">No available slots for this day.</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingPage;
